import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AcademicCapIcon,
  CalendarIcon,
  ChartBarIcon,
  ArrowRightOnRectangleIcon,
  UserIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowTrendingUpIcon,
  InformationCircleIcon,
  TrophyIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import studentAuthService from '../../../services/studentAuth';
import confetti from 'canvas-confetti';
import FloatingChatButton from '../../FloatingChatButton';
import FestivalWidget from '../../FestivalWidget';

// Custom Rupee Icon Component - Hidden for future use
// const RupeeIcon = ({ className }) => (
//   <div className={className + " flex items-center justify-center"}>
//     <span className="font-bold text-current text-3xl sm:text-4xl lg:text-5xl">₹</span>
//   </div>
// );

const StudentDashboard = () => {
  const navigate = useNavigate();
  const [student, setStudent] = useState(null);
  const [attendance, setAttendance] = useState([]);
  const [tests, setTests] = useState([]);
  const [rankData, setRankData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPassRateInfo, setShowPassRateInfo] = useState(false);
  const [showAvgScoreInfo, setShowAvgScoreInfo] = useState(false);

  useEffect(() => {
    loadDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadDashboardData = async () => {
    try {
      const [studentInfo, attendanceData, testsData, rank] = await Promise.all([
        studentAuthService.getStudentInfo(),
        studentAuthService.getAttendance(0, 10),
        studentAuthService.getTests(0, 10),
        studentAuthService.getMyRank(),
      ]);

      setStudent(studentInfo);
      setAttendance(attendanceData);
      setTests(testsData);
      setRankData(rank);

      // Check if today is student's birthday and trigger confetti
      if (studentInfo && isBirthday(studentInfo.date_of_birth)) {
        triggerBirthdayConfetti();
      }
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const isBirthday = (dateOfBirth) => {
    if (!dateOfBirth) return false;

    // Get current date in IST
    const now = new Date();
    const todayIST = now.toLocaleDateString('en-US', {
      timeZone: 'Asia/Kolkata',
      month: '2-digit',
      day: '2-digit'
    });

    // Treat date of birth as UTC
    const utcString = dateOfBirth.endsWith('Z') ? dateOfBirth : dateOfBirth + 'Z';
    const dob = new Date(utcString);
    const dobIST = dob.toLocaleDateString('en-US', {
      timeZone: 'Asia/Kolkata',
      month: '2-digit',
      day: '2-digit'
    });

    return todayIST === dobIST;
  };

  const triggerBirthdayConfetti = () => {
    const duration = 5 * 1000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 };

    function randomInRange(min, max) {
      return Math.random() * (max - min) + min;
    }

    const interval = setInterval(function() {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        return clearInterval(interval);
      }

      const particleCount = 50 * (timeLeft / duration);

      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 }
      });
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 }
      });
    }, 250);
  };

  const handleLogout = () => {
    studentAuthService.logout();
    navigate('/student/login');
  };

  const formatDate = (dateString) => {
    // Treat the datetime string as UTC by appending 'Z' if not present
    const utcString = dateString.endsWith('Z') ? dateString : dateString + 'Z';
    const date = new Date(utcString);

    // Get IST date string
    const istDateStr = date.toLocaleDateString('en-US', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });

    // Get today's IST date string
    const now = new Date();
    const todayISTStr = now.toLocaleDateString('en-US', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });

    // Get yesterday's IST date string
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayISTStr = yesterday.toLocaleDateString('en-US', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });

    if (istDateStr === todayISTStr) {
      return 'Today';
    } else if (istDateStr === yesterdayISTStr) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        timeZone: 'Asia/Kolkata'
      });
    }
  };

  const formatTime = (dateString) => {
    // Treat the datetime string as UTC by appending 'Z' if not present
    const utcString = dateString.endsWith('Z') ? dateString : dateString + 'Z';
    return new Date(utcString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Asia/Kolkata'
    });
  };

  const calculateAverageScore = () => {
    if (tests.length === 0) return 0;
    const sum = tests.reduce((acc, test) => acc + test.score_percentage, 0);
    return Math.round(sum / tests.length);
  };

  const getPassRate = () => {
    if (tests.length === 0) return 0;
    const passed = tests.filter(test => test.is_passed).length;
    return Math.round((passed / tests.length) * 100);
  };

  const formatDuration = (minutes) => {
    if (!minutes) return null;
    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      if (remainingMinutes === 0) {
        return `${hours} hr${hours > 1 ? 's' : ''}`;
      }
      return `${hours} hr${hours > 1 ? 's' : ''} ${remainingMinutes} min`;
    }
    return `${minutes} min`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <>
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      {/* Header */}
      <div className="bg-gray-800/50 backdrop-blur-sm border-b border-gray-700/50 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-3 sm:py-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="bg-gradient-to-br from-blue-500 to-purple-600 p-2 rounded-lg">
                <AcademicCapIcon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
              </div>
              <div>
                <h1 className="text-lg sm:text-xl font-bold text-white">Student Portal</h1>
                <p className="text-xs sm:text-sm text-gray-400 truncate max-w-[150px] sm:max-w-none">
                  {student?.first_name} {student?.last_name}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              {/* AI Assistant Button */}
              <button
                onClick={() => navigate('/student/ai-assistant')}
                className="flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-lg transition-all hover:scale-105 text-sm sm:text-base shadow-lg"
              >
                <SparklesIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                <span className="hidden sm:inline">AI Assistant</span>
              </button>
              {/* Logout Button */}
              <button
                onClick={handleLogout}
                className="flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-all hover:scale-105 text-sm sm:text-base"
              >
                <ArrowRightOnRectangleIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
        {/* Welcome Card */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl sm:rounded-2xl p-4 sm:p-6 mb-4 sm:mb-6 shadow-lg">
          <h2 className="text-xl sm:text-2xl font-bold text-white mb-1 sm:mb-2">
            Welcome back, {student?.first_name}! 👋
          </h2>
          <p className="text-blue-100 text-sm sm:text-base">
            Here's your learning progress at a glance
          </p>
        </div>

        {/* Festival Widget */}
        <div className="mb-4 sm:mb-6">
          <FestivalWidget />
        </div>

        {/* Birthday Banner */}
        {student && isBirthday(student.date_of_birth) && (
          <div className="mb-4 sm:mb-6 bg-gradient-to-r from-pink-600/20 via-purple-600/20 to-blue-600/20 backdrop-blur-sm rounded-2xl border-2 border-pink-500/50 p-4 sm:p-6 animate-pulse shadow-xl">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-center">
              <div className="text-4xl sm:text-6xl animate-bounce">🎂🎉🎈</div>
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-pink-400 via-purple-400 to-blue-400 mb-2">
                  Happy Birthday, {student.first_name}! 🎊
                </h2>
                <p className="text-gray-300 text-sm sm:text-base">
                  Wishing you an amazing day filled with joy and success! 🌟
                </p>
              </div>
              <div className="text-4xl sm:text-6xl animate-bounce" style={{ animationDelay: '0.2s' }}>🎁🎊🥳</div>
            </div>
          </div>
        )}

        {/* Rank Display Card */}
        {rankData && rankData.rank && (
          <div
            className={`mb-4 sm:mb-6 bg-gradient-to-r ${
              rankData.rank === 1
                ? 'from-yellow-600 via-amber-500 to-yellow-600'
                : rankData.rank === 2
                ? 'from-gray-400 via-gray-300 to-gray-400'
                : rankData.rank === 3
                ? 'from-orange-600 via-amber-700 to-orange-600'
                : rankData.rank_category === 'Top 10'
                ? 'from-purple-600 via-pink-500 to-purple-600'
                : rankData.percentile >= 75
                ? 'from-blue-600 via-cyan-500 to-blue-600'
                : rankData.percentile >= 50
                ? 'from-green-600 via-emerald-500 to-green-600'
                : 'from-gray-700 via-gray-600 to-gray-700'
            } rounded-2xl p-1 shadow-2xl hover:scale-[1.02] transition-all duration-300 cursor-pointer`}
            onClick={() => navigate('/student/top-performers')}
          >
            <div className="bg-gray-900/90 backdrop-blur-sm rounded-xl p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                {/* Left Side - Rank Badge */}
                <div className="flex items-center gap-4 sm:gap-6">
                  <div className={`relative flex items-center justify-center w-20 h-20 sm:w-24 sm:h-24 rounded-2xl bg-gradient-to-br ${
                    rankData.rank === 1
                      ? 'from-yellow-500 to-amber-600'
                      : rankData.rank === 2
                      ? 'from-gray-300 to-gray-500'
                      : rankData.rank === 3
                      ? 'from-orange-500 to-amber-700'
                      : 'from-blue-500 to-purple-600'
                  } shadow-xl`}>
                    <div className="absolute inset-0 bg-gradient-to-tr from-white/20 to-transparent rounded-2xl"></div>
                    <div className="text-center z-10">
                      <div className="text-3xl sm:text-4xl mb-1">
                        {rankData.rank === 1 ? '🥇' : rankData.rank === 2 ? '🥈' : rankData.rank === 3 ? '🥉' : '🏆'}
                      </div>
                      <div className="text-white font-bold text-lg sm:text-xl">#{rankData.rank}</div>
                    </div>
                  </div>

                  {/* Rank Details */}
                  <div className="text-center sm:text-left">
                    <h3 className="text-xl sm:text-2xl font-bold text-white mb-1 flex items-center gap-2 justify-center sm:justify-start">
                      {rankData.rank_category === 'Top 3' && '🌟 Top 3 Performer!'}
                      {rankData.rank_category === 'Top 10' && '⭐ Top 10 Performer!'}
                      {rankData.rank_category === 'Top 25%' && '🎯 Top 25% Performer!'}
                      {rankData.rank_category === 'Top 50%' && '📈 Top 50% Performer!'}
                      {rankData.rank_category === 'Keep Going' && '💪 Keep Pushing!'}
                    </h3>
                    <p className="text-gray-300 text-sm sm:text-base">
                      You're ranked <span className="font-bold text-white">#{rankData.rank}</span> out of{' '}
                      <span className="font-bold text-white">{rankData.total_students}</span> students
                    </p>
                    <div className="flex flex-wrap gap-2 mt-2 justify-center sm:justify-start">
                      <span className="bg-white/10 text-white px-3 py-1 rounded-full text-xs sm:text-sm font-medium">
                        {rankData.percentile}th Percentile
                      </span>
                      <span className="bg-white/10 text-white px-3 py-1 rounded-full text-xs sm:text-sm font-medium">
                        {rankData.average_score}% Avg Score
                      </span>
                    </div>
                  </div>
                </div>

                {/* Right Side - Motivational Message */}
                <div className="text-center sm:text-right">
                  <div className="text-3xl sm:text-4xl mb-2">
                    {rankData.rank === 1 ? '👑' : rankData.rank <= 3 ? '🎖️' : rankData.percentile >= 75 ? '🔥' : '💫'}
                  </div>
                  <p className="text-white/80 text-xs sm:text-sm italic max-w-[200px]">
                    {rankData.rank === 1 && "You're the champion!"}
                    {rankData.rank === 2 && "Excellence in action!"}
                    {rankData.rank === 3 && "Outstanding performance!"}
                    {rankData.rank > 3 && rankData.rank <= 10 && "You're doing amazing!"}
                    {rankData.rank > 10 && rankData.percentile >= 75 && "Great work, keep it up!"}
                    {rankData.rank > 10 && rankData.percentile >= 50 && rankData.percentile < 75 && "Good progress!"}
                    {rankData.percentile < 50 && "Keep pushing forward!"}
                  </p>
                  <button
                    className="mt-2 text-xs sm:text-sm text-white/60 hover:text-white underline"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate('/student/top-performers');
                    }}
                  >
                    View Full Leaderboard →
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Quick Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 mb-4 sm:mb-6 lg:mb-8">
          <div className="bg-gray-800/50 backdrop-blur-sm rounded-lg sm:rounded-xl p-3 sm:p-4 lg:p-6 border border-gray-700/50 hover:border-green-500/50 transition-all hover:scale-105">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div className="mb-2 sm:mb-0">
                <p className="text-gray-400 text-xs sm:text-sm">Attendance</p>
                <p className="text-2xl sm:text-3xl font-bold text-white mt-1">{attendance.length}</p>
              </div>
              <CalendarIcon className="h-8 w-8 sm:h-10 sm:w-10 lg:h-12 lg:w-12 text-green-500 opacity-80" />
            </div>
          </div>

          <div className="bg-gray-800/50 backdrop-blur-sm rounded-lg sm:rounded-xl p-3 sm:p-4 lg:p-6 border border-gray-700/50 hover:border-blue-500/50 transition-all hover:scale-105">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div className="mb-2 sm:mb-0">
                <p className="text-gray-400 text-xs sm:text-sm">Tests</p>
                <p className="text-2xl sm:text-3xl font-bold text-white mt-1">{tests.length}</p>
              </div>
              <ChartBarIcon className="h-8 w-8 sm:h-10 sm:w-10 lg:h-12 lg:w-12 text-blue-500 opacity-80" />
            </div>
          </div>

          <div className="bg-gray-800/50 backdrop-blur-sm rounded-lg sm:rounded-xl p-3 sm:p-4 lg:p-6 border border-gray-700/50 hover:border-purple-500/50 transition-all hover:scale-105 relative">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div className="mb-2 sm:mb-0">
                <div className="flex items-center gap-1.5">
                  <p className="text-gray-400 text-xs sm:text-sm">Avg Score</p>
                  <div className="relative">
                    <InformationCircleIcon
                      className="h-4 w-4 text-gray-500 cursor-help"
                      onMouseEnter={() => setShowAvgScoreInfo(true)}
                      onMouseLeave={() => setShowAvgScoreInfo(false)}
                      onClick={() => setShowAvgScoreInfo(!showAvgScoreInfo)}
                    />
                    {showAvgScoreInfo && (
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 sm:w-64 bg-gray-900 border border-gray-700 rounded-lg p-3 shadow-xl z-50">
                        <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-900 border-b border-r border-gray-700 transform rotate-45"></div>
                        <p className="text-xs text-gray-300 leading-relaxed">
                          Average score percentage across all your tests.
                          <span className="block mt-1 text-purple-400 font-medium">
                            Example: (85% + 90% + 75%) / 3 = 83%
                          </span>
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                <p className="text-2xl sm:text-3xl font-bold text-white mt-1">{calculateAverageScore()}%</p>
              </div>
              <ArrowTrendingUpIcon className="h-8 w-8 sm:h-10 sm:w-10 lg:h-12 lg:w-12 text-purple-500 opacity-80" />
            </div>
          </div>

          <div className="bg-gray-800/50 backdrop-blur-sm rounded-lg sm:rounded-xl p-3 sm:p-4 lg:p-6 border border-gray-700/50 hover:border-yellow-500/50 transition-all hover:scale-105 relative">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div className="mb-2 sm:mb-0">
                <div className="flex items-center gap-1.5">
                  <p className="text-gray-400 text-xs sm:text-sm">Pass Rate</p>
                  <div className="relative">
                    <InformationCircleIcon
                      className="h-4 w-4 text-gray-500 cursor-help"
                      onMouseEnter={() => setShowPassRateInfo(true)}
                      onMouseLeave={() => setShowPassRateInfo(false)}
                      onClick={() => setShowPassRateInfo(!showPassRateInfo)}
                    />
                    {showPassRateInfo && (
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 sm:w-64 bg-gray-900 border border-gray-700 rounded-lg p-3 shadow-xl z-50">
                        <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-900 border-b border-r border-gray-700 transform rotate-45"></div>
                        <p className="text-xs text-gray-300 leading-relaxed">
                          Percentage of tests you've passed out of total tests taken.
                          <span className="block mt-1 text-yellow-400 font-medium">
                            Example: 8 passed / 10 total = 80%
                          </span>
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                <p className="text-2xl sm:text-3xl font-bold text-white mt-1">{getPassRate()}%</p>
              </div>
              <CheckCircleIcon className="h-8 w-8 sm:h-10 sm:w-10 lg:h-12 lg:w-12 text-yellow-500 opacity-80" />
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 mb-4 sm:mb-6 lg:mb-8">
          <button
            onClick={() => navigate('/student/profile')}
            className="bg-gradient-to-br from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 rounded-xl p-4 sm:p-6 transition-all hover:scale-105 text-left shadow-lg group"
          >
            <UserIcon className="h-8 w-8 sm:h-10 sm:w-10 text-white mb-2 sm:mb-3 group-hover:scale-110 transition-transform" />
            <h3 className="text-lg sm:text-xl font-bold text-white mb-1 sm:mb-2">My Profile</h3>
            <p className="text-purple-100 text-sm">View and update your info</p>
          </button>

          <button
            onClick={() => navigate('/student/attendance')}
            className="bg-gradient-to-br from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 rounded-xl p-4 sm:p-6 transition-all hover:scale-105 text-left shadow-lg group"
          >
            <CalendarIcon className="h-8 w-8 sm:h-10 sm:w-10 text-white mb-2 sm:mb-3 group-hover:scale-110 transition-transform" />
            <h3 className="text-lg sm:text-xl font-bold text-white mb-1 sm:mb-2">Attendance</h3>
            <p className="text-green-100 text-sm">Check your attendance</p>
          </button>

          <button
            onClick={() => navigate('/student/tests')}
            className="bg-gradient-to-br from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 rounded-xl p-4 sm:p-6 transition-all hover:scale-105 text-left shadow-lg group"
          >
            <ChartBarIcon className="h-8 w-8 sm:h-10 sm:w-10 text-white mb-2 sm:mb-3 group-hover:scale-110 transition-transform" />
            <h3 className="text-lg sm:text-xl font-bold text-white mb-1 sm:mb-2">Test Results</h3>
            <p className="text-blue-100 text-sm">View your performance</p>
          </button>

          <button
            onClick={() => navigate('/student/top-performers')}
            className="bg-gradient-to-br from-yellow-600 to-orange-600 hover:from-yellow-700 hover:to-orange-700 rounded-xl p-4 sm:p-6 transition-all hover:scale-105 text-left shadow-lg group"
          >
            <TrophyIcon className="h-8 w-8 sm:h-10 sm:w-10 text-white mb-2 sm:mb-3 group-hover:scale-110 transition-transform" />
            <h3 className="text-lg sm:text-xl font-bold text-white mb-1 sm:mb-2">Top Performers</h3>
            <p className="text-yellow-100 text-sm">View leaderboard</p>
          </button>

          {/* Fees section hidden but component kept intact */}
          {/* <button
            onClick={() => navigate('/student/fees')}
            className="bg-gradient-to-br from-yellow-600 to-orange-600 hover:from-yellow-700 hover:to-orange-700 rounded-xl p-4 sm:p-6 transition-all hover:scale-105 text-left shadow-lg group"
          >
            <RupeeIcon className="h-8 w-8 sm:h-10 sm:w-10 text-white mb-2 sm:mb-3 group-hover:scale-110 transition-transform" />
            <h3 className="text-lg sm:text-xl font-bold text-white mb-1 sm:mb-2">My Fees</h3>
            <p className="text-yellow-100 text-sm">View payments & dues</p>
          </button> */}
        </div>

        {/* Recent Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          {/* Recent Attendance */}
          <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-700/50 p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg sm:text-xl font-bold text-white flex items-center gap-2">
                <CalendarIcon className="h-5 w-5 sm:h-6 sm:w-6 text-green-500" />
                Recent Attendance
              </h3>
              {attendance.length > 3 && (
                <button
                  onClick={() => navigate('/student/attendance')}
                  className="text-blue-400 hover:text-blue-300 text-xs sm:text-sm font-medium"
                >
                  View All
                </button>
              )}
            </div>
            {attendance.length > 0 ? (
              <div className="space-y-2 sm:space-y-3">
                {attendance.slice(0, 5).map((record, index) => (
                  <div
                    key={index}
                    className="bg-gray-700/50 rounded-lg p-3 sm:p-4 hover:bg-gray-700 transition-colors"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center gap-2">
                        <div className="bg-green-500/20 p-1.5 rounded">
                          <ClockIcon className="h-4 w-4 text-green-400" />
                        </div>
                        <p className="text-white font-medium text-sm sm:text-base">
                          {formatDate(record.date)}
                        </p>
                      </div>
                      {record.duration_minutes && (
                        <span className="text-xs sm:text-sm text-gray-400 bg-gray-600/50 px-2 py-1 rounded">
                          {formatDuration(record.duration_minutes)}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-400">
                      <span>{formatTime(record.check_in_time)}</span>
                      <span>→</span>
                      <span>
                        {record.check_out_time ? formatTime(record.check_out_time) : 'In Progress'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 sm:py-12">
                <CalendarIcon className="h-12 w-12 sm:h-16 sm:w-16 text-gray-600 mx-auto mb-3 sm:mb-4" />
                <p className="text-gray-400 text-sm sm:text-base">No attendance records yet</p>
              </div>
            )}
          </div>

          {/* Recent Tests */}
          <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-700/50 p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg sm:text-xl font-bold text-white flex items-center gap-2">
                <ChartBarIcon className="h-5 w-5 sm:h-6 sm:w-6 text-blue-500" />
                Recent Tests
              </h3>
              {tests.length > 3 && (
                <button
                  onClick={() => navigate('/student/tests')}
                  className="text-blue-400 hover:text-blue-300 text-xs sm:text-sm font-medium"
                >
                  View All
                </button>
              )}
            </div>
            {tests.length > 0 ? (
              <div className="space-y-2 sm:space-y-3">
                {tests.slice(0, 5).map((test, index) => (
                  <div
                    key={index}
                    className="bg-gray-700/50 rounded-lg p-3 sm:p-4 hover:bg-gray-700 transition-colors"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <p className="text-white font-medium text-sm sm:text-base flex-1 pr-2">
                        {test.test_title}
                      </p>
                      <div className="flex items-center gap-1.5">
                        {test.is_passed ? (
                          <CheckCircleIcon className="h-4 w-4 sm:h-5 sm:w-5 text-green-400" />
                        ) : (
                          <XCircleIcon className="h-4 w-4 sm:h-5 sm:w-5 text-red-400" />
                        )}
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs sm:text-sm text-gray-400">
                        {test.correct_answers}/{test.total_questions} correct
                      </span>
                      <span
                        className={`text-sm sm:text-base font-bold ${
                          test.score_percentage >= 75
                            ? 'text-green-400'
                            : test.score_percentage >= 50
                            ? 'text-yellow-400'
                            : 'text-red-400'
                        }`}
                      >
                        {test.score_percentage}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 sm:py-12">
                <ChartBarIcon className="h-12 w-12 sm:h-16 sm:w-16 text-gray-600 mx-auto mb-3 sm:mb-4" />
                <p className="text-gray-400 text-sm sm:text-base">No test records yet</p>
              </div>
            )}
          </div>
        </div>
      </div>


    </div>
    {/* Floating Chat Button - Outside main container for proper positioning */}
    <FloatingChatButton />
    </>
  );
};

export default StudentDashboard;
